/* eslint-disable react/jsx-indent */
/* eslint-disable indent */
import React, { useState } from 'react';
import { AddIcon, DeleteIcon } from '@chakra-ui/icons';
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  IconButton,
  Input,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Select,
  Spinner,
  Text,
  Textarea,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { CREATE_FEATURE_FLAGS } from '../../utils/constants';
import { getFirebaseIdToken } from '../../utils/utils';
import { FeatureFlag } from './feature-flag-card.component';

type FlagType = 'boolean' | 'string' | 'number';

interface CreateFeatureFlagsProps {
  setCurrentScreen: (screen: string) => void;
  selectedFlag: FeatureFlag | null;
}

const CreateFeatureFlags: React.FC<CreateFeatureFlagsProps> = ({
  setCurrentScreen,
  selectedFlag,
}) => {
  const [name, setName] = useState(selectedFlag?.name || '');
  const [flagType, setFlagType] = useState<FlagType>(
    selectedFlag?.valueType || 'boolean'
  );
  const [defaultValue, setDefaultValue] = useState<boolean | string | number>(
    selectedFlag?.defaultValue ?? false
  );
  const [rules, setRules] = useState<any[]>(selectedFlag?.rules || []);
  const [isLoading, setIsLoading] = useState(false);

  const toast = useToast();

  const addRule = () => {
    setRules([
      ...rules,
      {
        type: 'inList',
        identifier: 'appId',
        list: [],
        percentage: 0,
        value: getDefaultValueForType(flagType),
      },
    ]);
  };

  const removeRule = (index: number) => {
    setRules(rules.filter((_, i) => i !== index));
  };

  const updateRule = (index: number, field: string, value: any) => {
    const newRules = [...rules];
    newRules[index] = { ...newRules[index], [field]: value };
    setRules(newRules);
  };

  const getDefaultValueForType = (type: FlagType) => {
    switch (type) {
      case 'boolean':
        return false;
      case 'string':
        return '';
      case 'number':
        return 0;
    }
  };

  const handleFlagTypeChange = (newType: FlagType) => {
    setFlagType(newType);
    setDefaultValue(getDefaultValueForType(newType));
    setRules(rules.map((rule) => ({ ...rule, value: getDefaultValueForType(newType) })));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      setIsLoading(true);
      if (!name.trim()) {
        toast({
          title: 'Error',
          description: 'Name is required',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
        return;
      }

      const accessToken = await getFirebaseIdToken();
      const response = await fetch(CREATE_FEATURE_FLAGS, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name,
          type: flagType,
          defaultValue,
          rules,
        }),
      });
      if (!response.ok) {
        throw new Error('Failed to create feature flag');
      }

      toast({
        title: 'Success',
        description: 'Feature flag created successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
      setName('');
      setFlagType('boolean');
      setDefaultValue(false);
      setRules([]);
      setCurrentScreen('listAllFlags');
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.response?.data?.error || 'Failed to create feature flag',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box as="form" onSubmit={handleSubmit} position="relative">
      {isLoading && (
        <Flex
          position="absolute"
          top="0"
          left="0"
          right="0"
          bottom="0"
          justify="center"
          align="center"
          zIndex="overlay"
        >
          <Spinner />
        </Flex>
      )}
      <VStack spacing={4} align="stretch">
        <FormControl isRequired>
          <FormLabel>Name</FormLabel>
          <Input
            value={name}
            disabled={selectedFlag !== null}
            onChange={(e) => setName(e.target.value)}
          />
        </FormControl>

        <FormControl>
          <FormLabel>Flag Type</FormLabel>
          <Select
            value={flagType}
            onChange={(e) => handleFlagTypeChange(e.target.value as FlagType)}
          >
            <option value="boolean">Boolean</option>
            <option value="string">String</option>
            <option value="number">Number</option>
          </Select>
        </FormControl>

        <FormControl>
          <FormLabel>Default Value</FormLabel>
          {flagType === 'boolean' && (
            <Select
              value={defaultValue.toString()}
              onChange={(e) => setDefaultValue(e.target.value === 'true')}
            >
              <option value="true">True</option>
              <option value="false">False</option>
            </Select>
          )}
          {flagType === 'string' && (
            <Input
              value={defaultValue as string}
              onChange={(e) => setDefaultValue(e.target.value)}
            />
          )}
          {flagType === 'number' && (
            <NumberInput
              value={defaultValue as number}
              onChange={(_, value) => setDefaultValue(value)}
            >
              <NumberInputField />
              <NumberInputStepper>
                <NumberIncrementStepper />
                <NumberDecrementStepper />
              </NumberInputStepper>
            </NumberInput>
          )}
        </FormControl>

        <Text fontWeight="bold">Rules</Text>
        {rules.map((rule, index) => (
          <Box key={index} p={4} borderWidth={1} borderRadius="md">
            <HStack justify="space-between" mb={2}>
              <Text fontWeight="semibold">
                Rule
                {index + 1}
              </Text>
              <IconButton
                icon={<DeleteIcon />}
                onClick={() => removeRule(index)}
                aria-label="Remove rule"
              />
            </HStack>
            <VStack spacing={3} align="stretch">
              <FormControl>
                <FormLabel>Type</FormLabel>
                <Select
                  value={rule.type}
                  onChange={(e) => updateRule(index, 'type', e.target.value)}
                >
                  <option value="inList">In List</option>
                  <option value="random">Random</option>
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel>Identifier</FormLabel>
                <Select
                  value={rule.identifier}
                  onChange={(e) => updateRule(index, 'identifier', e.target.value)}
                >
                  <option value="appId">App ID</option>
                  <option value="providerId">Provider ID</option>
                  <option value="publicKey">Public Key</option>
                  <option value="sessionId">Session ID</option>
                </Select>
              </FormControl>

              {rule.type === 'inList' && (
                <FormControl>
                  <FormLabel>List (comma-separated)</FormLabel>
                  <Textarea
                    minHeight="100px"
                    value={rule.list.join(',')}
                    onChange={(e) =>
                      updateRule(
                        index,
                        'list',
                        e.target.value.split(',').map((item) => item.trim())
                      )
                    }
                  />
                </FormControl>
              )}

              {rule.type === 'random' && (
                <FormControl>
                  <FormLabel>Percentage</FormLabel>
                  <NumberInput
                    min={0}
                    max={100}
                    value={rule.percentage}
                    onChange={(valueString) => {
                      const value = parseFloat(valueString);
                      updateRule(index, 'percentage', isNaN(value) ? 0 : value);
                    }}
                  >
                    <NumberInputField />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                </FormControl>
              )}

              <FormControl>
                <FormLabel>Rule Value</FormLabel>
                {flagType === 'boolean' && (
                  <Select
                    value={rule.value.toString()}
                    onChange={(e) =>
                      updateRule(index, 'value', e.target.value === 'true')
                    }
                  >
                    <option value="true">True</option>
                    <option value="false">False</option>
                  </Select>
                )}
                {flagType === 'string' && (
                  <Input
                    value={rule.value as string}
                    onChange={(e) => updateRule(index, 'value', e.target.value)}
                  />
                )}
                {flagType === 'number' && (
                  <NumberInput
                    value={rule.value as number}
                    onChange={(_, value) => updateRule(index, 'value', value)}
                  >
                    <NumberInputField />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                )}
              </FormControl>
            </VStack>
          </Box>
        ))}

        <Button leftIcon={<AddIcon />} onClick={addRule} isLoading={isLoading}>
          Add Rule
        </Button>

        <Button type="submit" colorScheme="blue" isLoading={isLoading}>
          {selectedFlag ? 'Update Feature Flag' : 'Create Feature Flag'}
        </Button>
      </VStack>
    </Box>
  );
};

export default CreateFeatureFlags;
